/**
 * Toast notification system using Radix UI Toast.
 */
import * as React from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { create } from 'zustand'

// ─── Toast store ──────────────────────────────────────────────────────────────

interface ToastItem {
  id: string
  title: string
  description?: string
  variant?: 'default' | 'success' | 'error' | 'warning'
  duration?: number
}

interface ToastStore {
  toasts: ToastItem[]
  add: (toast: Omit<ToastItem, 'id'>) => void
  remove: (id: string) => void
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  add: (toast) =>
    set((s) => ({
      toasts: [...s.toasts, { ...toast, id: crypto.randomUUID() }],
    })),
  remove: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

/** Convenience hook to fire toasts. */
export function useToast() {
  const add = useToastStore((s) => s.add)
  return {
    toast: (opts: Omit<ToastItem, 'id'>) => add(opts),
    success: (title: string, description?: string) =>
      add({ title, description, variant: 'success' }),
    error: (title: string, description?: string) =>
      add({ title, description, variant: 'error' }),
    warning: (title: string, description?: string) =>
      add({ title, description, variant: 'warning' }),
  }
}

// ─── Toast UI ─────────────────────────────────────────────────────────────────

const variantClasses = {
  default: 'bg-white border-gray-200',
  success: 'bg-green-50 border-green-200',
  error: 'bg-red-50 border-red-200',
  warning: 'bg-yellow-50 border-yellow-200',
}

const titleClasses = {
  default: 'text-gray-900',
  success: 'text-green-800',
  error: 'text-red-800',
  warning: 'text-yellow-800',
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { toasts, remove } = useToastStore()

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {children}
      {toasts.map((toast) => (
        <ToastPrimitive.Root
          key={toast.id}
          duration={toast.duration ?? 4000}
          onOpenChange={(open) => !open && remove(toast.id)}
          className={cn(
            'flex items-start gap-3 rounded-lg border p-4 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-80 data-[state=open]:fade-in-0',
            'data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full',
            variantClasses[toast.variant ?? 'default'],
          )}
        >
          <div className="flex-1">
            <ToastPrimitive.Title
              className={cn('text-sm font-semibold', titleClasses[toast.variant ?? 'default'])}
            >
              {toast.title}
            </ToastPrimitive.Title>
            {toast.description && (
              <ToastPrimitive.Description className="mt-0.5 text-sm text-gray-600">
                {toast.description}
              </ToastPrimitive.Description>
            )}
          </div>
          <ToastPrimitive.Close
            className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </ToastPrimitive.Close>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[100] flex max-h-screen w-full max-w-sm flex-col gap-2" />
    </ToastPrimitive.Provider>
  )
}
